/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import { Combobox, IconButton, TextArea } from '@neo4j-ndl/react';
import { CommandLineIconOutline } from '@neo4j-ndl/react/icons';
import { useRef, useState } from 'react';

const commands = [
  {
    description: 'Return a small sample of nodes',
    label: 'Match nodes',
    value: 'MATCH (n) RETURN n LIMIT 25',
  },
  {
    description: 'Show the labels and relationships in the database',
    label: 'Visualize schema',
    value: 'CALL db.schema.visualization()',
  },
  {
    description: 'List the indexes available to the current user',
    label: 'Show indexes',
    value: 'SHOW INDEXES',
  },
];

const Component = () => {
  const [terminalValue, setTerminalValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  return (
    <div className="n-flex n-gap-token-2 n-items-center">
      <TextArea
        ref={textAreaRef}
        label="Command input"
        value={terminalValue}
        htmlAttributes={{
          onChange: (event) => setTerminalValue(event.target.value),
          rows: 5,
        }}
        helpText="Use the button below to search for a command."
      />
      <Combobox
        label="Insert command"
        options={commands}
        searchPlaceholder="Search commands"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        // The picker activates an option but never keeps it as its value.
        // Keeping the controlled value empty avoids a persistent selected state.
        value={null}
        onChange={(command) => {
          if (command === null) {
            return;
          }

          setTerminalValue((currentValue) =>
            currentValue === ''
              ? command.value
              : `${currentValue}\n${command.value}`,
          );
          setSearchValue('');
          textAreaRef.current?.focus();
        }}
        renderTrigger={({ ref, ...triggerProps }) => (
          <IconButton
            ref={ref as React.Ref<HTMLButtonElement>}
            description="Insert command"
            htmlAttributes={triggerProps}
          >
            <CommandLineIconOutline />
          </IconButton>
        )}
      />
    </div>
  );
};

export default Component;
